import { AssetKeys } from './AssetKeys'

/**
 * Slot 游戏核心配置 — Candy 6×5 Cluster + Tumble (ADR-015)
 *
 * 后端 packages/backend/src/games/configs/candy.json 是单一事实源，
 * 这里的 Grid 维度必须与之一致。后端配置变更时同步更新此处。
 */

/** Symbol 代码（与 backend `@sss/shared` SymbolKey 一一对应） */
export type SymbolKey =
  | 'CHERRY'
  | 'LEMON'
  | 'WATERMELON'
  | 'STRAWBERRY'
  | 'BLUEBERRY'
  | 'RAINBOW'
  | 'CROWN'
  | 'GOLDEN7'
  | 'WILD'
  | 'SCATTER'
  | 'MULTIPLIER'

/** 低值 / 高值分类（用于视觉打磨） */
export const LOW_SYMBOLS: SymbolKey[] = ['CHERRY', 'LEMON', 'WATERMELON', 'STRAWBERRY', 'BLUEBERRY']
export const HIGH_SYMBOLS: SymbolKey[] = ['RAINBOW', 'CROWN', 'GOLDEN7']

/**
 * SymbolKey → asset key 映射。
 * 默认 candy 主题；运行时由 setSymbolMap() 切到当前 game 的 manifest.symbolToAsset。
 */
export const SymbolToAsset: Record<SymbolKey, string> = {
  CHERRY:     AssetKeys.symbol.cherry,
  LEMON:      AssetKeys.symbol.lemon,
  WATERMELON: AssetKeys.symbol.watermelon,
  STRAWBERRY: AssetKeys.symbol.strawberry,
  BLUEBERRY:  AssetKeys.symbol.blueberry,
  RAINBOW:    AssetKeys.symbol.rainbow,
  CROWN:      AssetKeys.symbol.heart_gummy,
  GOLDEN7:    AssetKeys.symbol.star_candy,
  WILD:       AssetKeys.symbol.chocolate_square,
  SCATTER:    AssetKeys.symbol.lollipop_scatter,
  MULTIPLIER: AssetKeys.symbol.multiplier_orb,
}

/** 切换 SymbolKey → asset key 映射（由 BootScene 在 init 后按 game cfg 调用） */
export function setSymbolMap(map: Record<SymbolKey, string>): void {
  for (const k of Object.keys(map) as SymbolKey[]) {
    SymbolToAsset[k] = map[k]
  }
}

/** 全部 symbol 列表 — Reel 初始随机填充用（cluster_tumble 后端不会发 WILD） */
export const ALL_SYMBOLS: SymbolKey[] = [
  ...LOW_SYMBOLS,
  ...HIGH_SYMBOLS,
  'SCATTER',
]

/** Reel 网格几何 — 默认 Candy 6×5（cluster），运行时由 setGrid() 切换到当前 game */
export const Grid = {
  COLS: 6,
  ROWS: 5,
  CELL_WIDTH: 200,
  CELL_HEIGHT: 200,
  ORIGIN_X: -60,    // cell=200 × 6 = 1200，超出 canvas 60px 每边（边缘 symbol 会有少量裁切）
  ORIGIN_Y: 460,    // 保持 jackpot 在上方原位
}

/** 网格总尺寸（派生），随 setGrid() 同步更新 */
export const GridSize = {
  WIDTH:  Grid.COLS * Grid.CELL_WIDTH,
  HEIGHT: Grid.ROWS * Grid.CELL_HEIGHT,
}

/** Symbol 在格子内的尺寸（留 padding 显得不挤） — 随 setGrid() 同步更新 */
export const SymbolSizeBox = { value: 178 }
export const SYMBOL_SIZE = SymbolSizeBox.value

/**
 * 切换当前 game 的 grid 维度（被 BootScene 在 init 后调用）。
 * Reel zone 上下界 460–1240（高 780），grid 在其中垂直居中。
 * Canvas 1080 宽，水平居中。
 */
export function setGrid(cfg: { cols: number; rows: number; cellWidth: number; cellHeight: number }): void {
  Grid.COLS = cfg.cols
  Grid.ROWS = cfg.rows
  Grid.CELL_WIDTH = cfg.cellWidth
  Grid.CELL_HEIGHT = cfg.cellHeight
  GridSize.WIDTH = cfg.cols * cfg.cellWidth
  GridSize.HEIGHT = cfg.rows * cfg.cellHeight
  Grid.ORIGIN_X = Math.floor((1080 - GridSize.WIDTH) / 2)
  // Reel zone 460-1240; 在内部垂直居中
  const reelZoneTop = 460
  const reelZoneH = 780
  Grid.ORIGIN_Y = Math.floor(reelZoneTop + (reelZoneH - GridSize.HEIGHT) / 2)
  // Symbol size = cell - 18 padding（12% 内边距）
  SymbolSizeBox.value = Math.min(cfg.cellWidth, cfg.cellHeight) - 18
}

/** Reel 转动动画时序（毫秒） — 中间偏轻快
 *
 * 注意：本对象的属性可被 setSpinPreset() 在运行时覆写（turbo 切换）。
 * Reel / ReelGrid 直接读 SpinTiming.* 即可，热更新生效。
 */
export const SpinTiming = {
  ACCEL_MS: 220,
  CONSTANT_MIN_MS: 380,
  DECEL_MS: 540,
  STAGGER_MS: 160,
  MAX_SCROLL_SPEED: 3200,
}

/** Turbo 模式时序（明显加速但不"瞬停"） */
const SPIN_TIMING_NORMAL = {
  ACCEL_MS: 220,
  CONSTANT_MIN_MS: 380,
  DECEL_MS: 540,
  STAGGER_MS: 160,
  MAX_SCROLL_SPEED: 3200,
}

const SPIN_TIMING_TURBO = {
  ACCEL_MS: 90,
  CONSTANT_MIN_MS: 140,
  DECEL_MS: 240,
  STAGGER_MS: 60,
  MAX_SCROLL_SPEED: 5200,
}

/** 切换 SpinTiming 预设（turbo / normal） */
export function setSpinPreset(p: 'normal' | 'turbo'): void {
  Object.assign(SpinTiming, p === 'turbo' ? SPIN_TIMING_TURBO : SPIN_TIMING_NORMAL)
}

/** 下注档位（最低消耗 10K；需与 backend ALLOWED_BETS 同步） */
export const BET_LEVELS = [10_000, 20_000, 50_000, 100_000, 200_000, 500_000, 1_000_000] as const
export const DEFAULT_BET = 10_000

/** 起始虚拟金币 */
export const STARTING_BALANCE = 500_000

/** 主屏布局常量（设计分辨率 1080×1920） */
export const Layout = {
  CANVAS_W: 1080,
  CANVAS_H: 1920,

  /** 顶部条 (Menu | Logo | Settings) */
  TOP_BAR_Y: 50,

  /** Jackpot 阶梯 NC-017 (2172×724) → 1080×360 (保留原生比例)，center y=290 */
  JACKPOT_HEADER: { CX: 540, CY: 290, W: 1080, H: 360 },

  /** Reel zone 上下界 — 配合 cell=200 + origin_y=460，5 行高 1000 → 范围 460..1460 */
  REEL_ZONE_TOP: 460,
  REEL_ZONE_BOTTOM: 1460,

  /** HUD 数值条 (Balance/Bet/Win) */
  HUD_Y: 1300,

  /** 控制栏 NC-018 (2172×724) → 1080×360 (保留原生比例)，center y=1576 */
  CONTROL_BAR: { CX: 540, CY: 1576, W: 1080, H: 360 },

  /** Spin 按钮 — 实测 NC-018 右大圆 slot 中心 */
  SPIN_BTN: { CX: 845, CY: 1610, SIZE: 220 },

  /** Bet- / Bet+ — NC-018 两小圆 slot 像素扫描定位 */
  BET_MINUS_BTN: { CX: 125, CY: 1657, SIZE: 95 },
  BET_PLUS_BTN:  { CX: 225, CY: 1657, SIZE: 95 },

  /** 副栏 (Auto/Turbo/MaxBet/BuyBonus) — 控制栏下方 */
  UTIL_ROW_Y: 1830,
  UTIL_BTN_SIZE: 110,
  UTIL_BTN_AUTO_X: 200,
  UTIL_BTN_TURBO_X: 360,
  UTIL_BTN_MAXBET_X: 720,
  UTIL_BTN_BUYBONUS_X: 880,

  /** 顶部 Paytable 图标（Settings 旁） */
  PAYTABLE_BTN: { CX: 880, CY: 50, SIZE: 80 },
} as const
